import type { PayloadAction } from '@reduxjs/toolkit';
import type { IJob, JobsState } from './types';

import { createSlice } from '@reduxjs/toolkit';

const initialState: JobsState = {
  jobs: [],
  loading: false,
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    // Set all jobs (from initial fetch)
    setJobs: (state, action: PayloadAction<IJob[]>) => {
      state.jobs = action.payload;
      state.loading = false;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Add or update a job
    upsertJob: (state, action: PayloadAction<IJob>) => {
      const index = state.jobs.findIndex((job) => job.jobId === action.payload.jobId);
      if (index !== -1) {
        state.jobs[index] = action.payload;
      } else {
        state.jobs.unshift(action.payload); // Add new jobs to the beginning
      }
    },

    // Update job progress and status (from Socket.io events)
    updateJobProgress: (state, action: PayloadAction<Partial<IJob> & { jobId: string }>) => {
      const job = state.jobs.find((j) => j.jobId === action.payload.jobId);
      if (job) {
        Object.assign(job, action.payload);
      }
    },

    // Remove completed/failed jobs after they've been visible for a while
    removeJob: (state, action: PayloadAction<string>) => {
      state.jobs = state.jobs.filter((job) => job.jobId !== action.payload);
    },

    // Clear all jobs
    clearJobs: (state) => {
      state.jobs = [];
    },

    // Remove old completed/failed jobs (older than 1 week)
    cleanupOldJobs: (state) => {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      state.jobs = state.jobs.filter((job) => {
        if (job.status === 'completed' || job.status === 'failed') {
          const jobDate = new Date(job.updated_at);
          return jobDate > oneWeekAgo;
        }
        return true; // Keep active/waiting jobs
      });
    },
  },
});

export const {
  setJobs,
  setLoading,
  upsertJob,
  updateJobProgress,
  removeJob,
  clearJobs,
  cleanupOldJobs,
} = jobsSlice.actions;

export default jobsSlice.reducer;
